import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { FileText, Shield, Users, Kanban, User, Building2 } from 'lucide-react';
import { useQuickLinkRecentPaths } from 'hooks/useQuickLinkRecentPaths';
import { useUserPreferencesStore } from 'stores/userPreferencesStore';
import { ROUTES } from 'utils/constants';
import { isBetaOnlyPath } from 'utils/betaFeatures';
import Button from 'components/Button';
import styles from './Dashboard.module.scss';

const LINK_ICON = 20;

type QuickLinkItem = { path: string; label: string; Icon: LucideIcon };

/** Default ordering for main features; merged after recent paths (deduped by path). */
const MAIN_FEATURE_FALLBACK: QuickLinkItem[] = [
  {
    path: ROUTES.REPORTS.INVENTORY_REPORT,
    label: 'Inventory Report',
    Icon: FileText,
  },
  {
    path: ROUTES.REPORTS.ROOT,
    label: 'Reports',
    Icon: FileText,
  },
  { path: ROUTES.PERMISSION.ROOT, label: 'Permissions', Icon: Shield },
  { path: ROUTES.ROLE.ROOT, label: 'Roles', Icon: Users },
  { path: ROUTES.KANBAN, label: 'Kanban', Icon: Kanban },
  { path: ROUTES.USER.PROFILE, label: 'My profile', Icon: User },
  { path: ROUTES.COMPANIES.ROOT, label: 'Companies', Icon: Building2 },
];

const LINK_META_BY_PATH: Map<string, QuickLinkItem> = new Map(
  MAIN_FEATURE_FALLBACK.map((item) => [item.path, item]),
);

function Dashboard() {
  const navigate = useNavigate();
  const recentPaths = useQuickLinkRecentPaths();
  const betaEnabled = useUserPreferencesStore((s) => s.betaEnabled);
  const [showAllQuickLinks, setShowAllQuickLinks] = useState(false);

  /** Recent paths first (priority), then the rest of the main features — no duplicate paths. */
  const quickLinks = useMemo((): QuickLinkItem[] => {
    const seen = new Set<string>();
    const out: QuickLinkItem[] = [];

    for (const p of recentPaths) {
      if (!betaEnabled && isBetaOnlyPath(p)) continue;
      const meta = LINK_META_BY_PATH.get(p);
      if (!meta || seen.has(p)) continue;
      seen.add(p);
      out.push(meta);
    }

    for (const item of MAIN_FEATURE_FALLBACK) {
      if (!betaEnabled && isBetaOnlyPath(item.path)) continue;
      if (seen.has(item.path)) continue;
      seen.add(item.path);
      out.push(item);
    }

    return out;
  }, [betaEnabled, recentPaths]);

  const visibleLinks = showAllQuickLinks ? quickLinks : quickLinks.slice(0, 6);
  const hasMoreLinks = quickLinks.length > 6;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>Dashboard</h1>
          </div>
          <p className={styles.subtitle}>Overview of your business</p>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.dashboardTopGrid}>
          <div
            className={`${styles.dashboardQuickLinksColumn} ${styles.gridCard}`}
          >
            <h2 className={styles.sectionTitle}>Quick Links</h2>
            <div className={styles.dashboardQuickLinksGrid}>
              {visibleLinks.map(({ path, label, Icon }) => (
                <Button
                  key={path}
                  type="button"
                  variant="outline"
                  className={styles.quickLink}
                  onClick={() => navigate(path)}
                >
                  <Icon size={LINK_ICON} aria-hidden />
                  {label}
                </Button>
              ))}
            </div>
            {hasMoreLinks && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAllQuickLinks((prev) => !prev)}
                style={{ marginTop: '1rem', width: '100%' }}
              >
                {showAllQuickLinks ? 'Show less' : 'Show more'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <section className={styles.toolsSection}>
          <h2 className={styles.toolsTitle}>
            Tools That Support Your Business
          </h2>
          <p className={styles.toolsSubtitle}>
            Choose the platform that fits your workflow and get productive
            faster.
          </p>
          <div className={styles.toolsGrid}>
            <article className={`${styles.toolsCard} ${styles.toolsCardTaxes}`}>
              <img
                src="/images/dashboard/taxesph-theme.png"
                alt="TaxesPH feature preview"
                className={styles.toolsImage}
                loading="lazy"
              />
              <h3 className={styles.toolsCardTitle}>TaxesPH</h3>
              <p className={styles.toolsCardText}>
                Online filing workspace with guided flows and compliance-ready
                outputs.
              </p>
              <a
                href="https://taxesph.com"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.toolsCardButton}
              >
                Visit taxesph.com
              </a>
            </article>
            <article
              className={`${styles.toolsCard} ${styles.toolsCardAbrafi}`}
            >
              <img
                src="/images/dashboard/abrafi-theme.png"
                alt="AbraFi feature preview"
                className={styles.toolsImage}
                loading="lazy"
              />
              <h3 className={styles.toolsCardTitle}>AbraFi</h3>
              <p className={styles.toolsCardText}>
                All-in-one bookkeeping and finance operations for modern
                business teams.
              </p>
              <a
                href="https://abrafi.ph"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.toolsCardButton}
              >
                Visit abrafi.ph
              </a>
            </article>
          </div>
        </section>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Tips & Best Practices</h2>
          <div className={styles.tipsList}>
            <div className={styles.tipItem}>
              <div className={styles.tipIcon}>📝</div>
              <div className={styles.tipContent}>
                <strong>Record Transactions Regularly:</strong> Enter your
                transactions promptly to maintain accurate and up-to-date
                financial records.
              </div>
            </div>
            <div className={styles.tipItem}>
              <div className={styles.tipIcon}>📊</div>
              <div className={styles.tipContent}>
                <strong>Review Reports:</strong> Check your income statement and
                balance sheet monthly to track your business performance and
                make informed decisions.
              </div>
            </div>
            <div className={styles.tipItem}>
              <div className={styles.tipIcon}>🏷️</div>
              <div className={styles.tipContent}>
                <strong>Maintain your chart of accounts:</strong> Keep accounts
                clear and consistent so transactions classify correctly in
                reports.
              </div>
            </div>
            <div className={styles.tipItem}>
              <div className={styles.tipIcon}>💼</div>
              <div className={styles.tipContent}>
                <strong>Track Sales & Expenses:</strong> Regularly record your
                sales and expenses to get a clear picture of your business
                financial health.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
